import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Sparkles, ArrowRight, Menu, X, BarChart3, Users, Zap, Shield, ChevronRight, Play } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import bravaxLogo from '@/assets/bravaxforms-logo.png';

const Landing = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [intent, setIntent] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBuild = () => {
    if (intent.trim()) {
      navigate(`/ai-builder?intent=${encodeURIComponent(intent.trim())}`);
    } else {
      navigate('/ai-builder');
    }
  };

  const features = [
    { 
      icon: Sparkles, 
      title: 'AI Form Generation', 
      desc: 'Describe your needs, and our AI constructs sophisticated forms with logical flows in seconds.',
      color: 'text-blue-500',
      bg: 'bg-blue-500/10'
    },
    { 
      icon: BarChart3, 
      title: 'Real-time Analytics', 
      desc: 'Gain deep insights into submission trends, conversion rates, and user behavior with automated reports.',
      color: 'text-purple-500',
      bg: 'bg-purple-500/10'
    },
    { 
      icon: Users, 
      title: 'Built-in CRM', 
      desc: 'Every submission is a lead. Automatically categorize and manage contacts in your unified workspace.',
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10'
    },
    { 
      icon: Shield, 
      title: 'Enterprise Security', 
      desc: 'Bank-grade encryption and granular RLS policies ensure your data remains private and secure.',
      color: 'text-orange-500',
      bg: 'bg-orange-500/10'
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground font-sans selection:bg-primary/30">
      <Helmet>
        <title>Bravax Forms — The AI-Native Form Builder</title>
        <meta name="description" content="Build premium, intelligent forms with AI. Collect data, analyze responses, and manage leads in a world-class interface." />
      </Helmet>

      {/* Background Decor */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/5 blur-[120px] rounded-full" />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] brightness-100 contrast-150" />
      </div>

      {/* Navigation */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-background/80 backdrop-blur-xl border-b border-border/40 py-3' : 'bg-transparent py-4'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-8">
              <img src={bravaxLogo} alt="Bravax Forms" className="h-7 cursor-pointer" onClick={() => navigate('/')} />
              <div className="hidden lg:flex items-center gap-6 text-sm font-medium text-muted-foreground">
                <a href="#features" className="hover:text-foreground transition-colors">Features</a>
                <a href="#solutions" className="hover:text-foreground transition-colors">Solutions</a>
                <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <ThemeToggle />
              <Button variant="ghost" className="text-sm font-medium" onClick={() => navigate('/login')}>
                Sign In
              </Button>
              <Button 
                onClick={() => navigate('/signup')} 
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-6 shadow-lg shadow-primary/20 transition-all hover:scale-[1.02]"
              >
                Get Started
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pt-4 pb-6 space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
              <div className="flex flex-col gap-2">
                <Button variant="ghost" className="justify-start" onClick={() => navigate('/login')}>Sign In</Button>
                <Button className="justify-start bg-primary text-primary-foreground" onClick={() => navigate('/signup')}>Get Started</Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      <main className="relative z-10">
        {/* Hero Section */}
        <section className="pt-16 pb-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold mb-6 sm:mb-8 animate-fade-in">
            <Sparkles className="h-3 w-3" />
            <span>AI-Native Platform</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-display font-extrabold tracking-tight mb-6 sm:mb-8 max-w-5xl mx-auto leading-[1.08]">
            Build better forms, <br className="hidden sm:block" />
            <span className="text-muted-foreground/40">faster than ever.</span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 sm:mb-12 leading-relaxed px-2">
            The intelligent way to collect data. Describe your form in plain language and let our AI architect the perfect flow for your audience.
          </p>

          {/* Prompt Input Box */}
          <div className="max-w-2xl mx-auto mb-12 sm:mb-16 group relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-blue-500/20 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200" />
            <div className="relative rounded-2xl border border-border/60 bg-card shadow-2xl overflow-hidden backdrop-blur-sm">
              <Textarea
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
                placeholder="e.g. A customer feedback form for my new SaaS product..."
                className="min-h-[100px] sm:min-h-[120px] border-0 bg-transparent resize-none text-base sm:text-lg focus-visible:ring-0 p-4 sm:p-6 pb-20 placeholder:text-muted-foreground/50"
                maxLength={500}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleBuild();
                  }
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 bg-background/40 backdrop-blur-md border-t border-border/20">
                <div className="flex items-center gap-2">
                   <div className="flex -space-x-2">
                      {[1,2,3].map(i => <div key={i} className="w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-card bg-muted" />)}
                   </div>
                   <span className="text-xs text-muted-foreground font-medium ml-1">Trusted by 1,000+ creators</span>
                </div>
                <Button
                  onClick={handleBuild}
                  className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground rounded-full gap-2 px-5 shadow-lg shadow-primary/20 h-9 sm:h-10 text-sm transition-all hover:scale-[1.03]"
                >
                  <Sparkles className="h-4 w-4" />
                  <span>Architect Form</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Product Preview */}
          <div className="relative max-w-6xl mx-auto mt-12 sm:mt-24">
            <div className="absolute -inset-4 bg-gradient-to-b from-primary/10 to-transparent blur-[100px] opacity-50" />
            <div className="relative rounded-xl sm:rounded-2xl border border-border/40 bg-card/30 backdrop-blur-xl shadow-2xl overflow-hidden aspect-[16/9] group">
              <div className="absolute inset-0 bg-gradient-to-tr from-background/80 via-transparent to-transparent z-10" />
              <img 
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=2426&ixlib=rb-4.0.3" 
                alt="Bravax Forms Dashboard" 
                className="w-full h-full object-cover opacity-90 group-hover:scale-[1.01] transition-transform duration-700"
              />
              <div className="absolute inset-0 flex items-center justify-center z-20">
                 <Button size="icon" variant="secondary" className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-background/80 backdrop-blur-md border border-border/50 shadow-2xl hover:scale-110 transition-all">
                    <Play className="h-5 w-5 sm:h-6 sm:w-6 fill-primary text-primary" />
                 </Button>
              </div>
              <div className="absolute bottom-4 sm:bottom-8 left-4 sm:left-8 right-4 sm:right-8 flex justify-between items-end z-20">
                 <div className="space-y-0.5 sm:space-y-1">
                    <h4 className="text-white font-bold text-sm sm:text-lg">Intelligent Analytics</h4>
                    <p className="text-white/60 text-xs sm:text-sm hidden sm:block">Visualize your data with AI-native insights.</p>
                 </div>
                 <div className="flex gap-1.5 sm:gap-2">
                    <div className="w-8 sm:w-12 h-1 rounded-full bg-primary" />
                    <div className="w-8 sm:w-12 h-1 rounded-full bg-white/20" />
                    <div className="w-8 sm:w-12 h-1 rounded-full bg-white/20" />
                 </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bento Grid Features */}
        <section id="features" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-20">
             <h2 className="text-2xl sm:text-3xl md:text-5xl font-display font-bold tracking-tight mb-4">Everything you need, <br /> nothing you don't.</h2>
             <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base">Focus on your business, not on building forms. Our platform handles the complexity.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((f, i) => (
              <div key={i} className="group p-6 sm:p-8 rounded-2xl border border-border/40 bg-card/50 backdrop-blur-sm hover:bg-card hover:border-primary/30 transition-all duration-300">
                <div className={`w-12 h-12 rounded-xl ${f.bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <f.icon className={`h-6 w-6 ${f.color}`} />
                </div>
                <h3 className="text-xl font-bold mb-3">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto text-center">
           <div className="relative p-8 sm:p-12 md:p-20 rounded-2xl sm:rounded-3xl overflow-hidden border border-border/40 bg-card/50 backdrop-blur-md">
              <div className="absolute top-0 right-0 p-6 sm:p-8">
                 <Sparkles className="h-8 w-8 sm:h-12 sm:w-12 text-primary/10" />
              </div>
              <h2 className="text-2xl sm:text-4xl md:text-6xl font-display font-bold mb-6 sm:mb-8">Ready to transform <br />your data collection?</h2>
              <Button 
                size="lg"
                onClick={() => navigate('/signup')} 
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-6 sm:px-10 h-12 sm:h-14 text-base sm:text-lg shadow-xl shadow-primary/20 w-full sm:w-auto"
              >
                Start building for free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <p className="mt-6 sm:mt-8 text-sm text-muted-foreground">No credit card required. Cancel anytime.</p>
           </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12 sm:py-16 px-4 sm:px-6 border-t border-border/20 bg-background/50 relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-10 sm:mb-16">
           <div className="col-span-2 md:col-span-1 text-left">
              <img src={bravaxLogo} alt="Bravax Forms" className="h-6 mb-4 sm:mb-6 opacity-80" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                 The world's most intelligent form builder. Powered by AI, designed for humans.
              </p>
           </div>
           <div className="text-left">
              <h4 className="font-bold mb-4 sm:mb-6 text-sm uppercase tracking-widest text-muted-foreground/60">Product</h4>
              <ul className="space-y-3 sm:space-y-4 text-sm text-muted-foreground">
                 <li><a href="#" className="hover:text-primary transition-colors">AI Builder</a></li>
                 <li><a href="#" className="hover:text-primary transition-colors">CRM Features</a></li>
                 <li><a href="#" className="hover:text-primary transition-colors">Analytics</a></li>
              </ul>
           </div>
           <div className="text-left">
              <h4 className="font-bold mb-4 sm:mb-6 text-sm uppercase tracking-widest text-muted-foreground/60">Company</h4>
              <ul className="space-y-3 sm:space-y-4 text-sm text-muted-foreground">
                 <li><a href="#" className="hover:text-primary transition-colors">About Us</a></li>
                 <li><a href="#" className="hover:text-primary transition-colors">Contact</a></li>
                 <li><a href="https://bravaxltd.com" className="hover:text-primary transition-colors">Bravax Ltd</a></li>
              </ul>
           </div>
           <div className="text-left">
              <h4 className="font-bold mb-4 sm:mb-6 text-sm uppercase tracking-widest text-muted-foreground/60">Legal</h4>
              <ul className="space-y-3 sm:space-y-4 text-sm text-muted-foreground">
                 <li><a href="#" className="hover:text-primary transition-colors">Privacy Policy</a></li>
                 <li><a href="#" className="hover:text-primary transition-colors">Terms of Service</a></li>
              </ul>
           </div>
        </div>
        <div className="max-w-7xl mx-auto pt-6 sm:pt-8 border-t border-border/10 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-muted-foreground/50">
          <span>&copy; 2026 Bravax Forms. All rights reserved.</span>
          <a href="https://bravaxltd.com" className="hover:text-muted-foreground transition-colors underline underline-offset-4">
            Developed by Bravax Technologies Limited.
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
